import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  ChatAttachmentUploadProcess,
  LogoBannerUploadProcess,
  StartResponse,
  startResponseSchema,
  FileUploadProcess,
  TenantInfographicUploadProcess,
  TenantLogoUploadProcess,
  ReportingUploadProcess,
} from './types';

import { restAPI } from '../rest-api';


const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    // UPLOAD ATTACHMENTS FROM MESSAGES
    uploadMessageAttachmentProcess: builder.mutation<
      { id: number | undefined },
      ChatAttachmentUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/report-chat/${arg.conversationId}/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startResMessageAttachmentObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startResMessageAttachmentObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startResMessageAttachmentObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            excludeAPIHeaders: true,
            baseURLOverride: startResMessageAttachmentObject.url,
            withCredentials: false,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          // Step 3: Finish upload
          const finish = await baseQuery({
            url: `/report-chat/${arg.conversationId}/upload/finish`,
            method: 'POST',
            data: {
              file_id: startResMessageAttachmentObject.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startResMessageAttachmentObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: true },
    }),
    UploadLogoBanerProcess: builder.mutation<
      { message: string },
      LogoBannerUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/intake-channel/tenant/${arg.slug}/${arg.selectedChannelId}/upload-start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startLogoBanerResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startLogoBanerResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startLogoBanerResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            baseURLOverride: startLogoBanerResObject.url,
            withCredentials: false,
            excludeAPIHeaders: true,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          const finish = await baseQuery({
            url: `/intake-channel/tenant/${arg.slug}/${arg.selectedChannelId}/upload-finish`,
            method: 'POST',
            data: {
              key: startLogoBanerResObject.fields?.key,
              upload_type: arg.uploadType,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
        }
        return { data: { message: 'success' } };
      },
      extraOptions: { requireAuth: true },
      invalidatesTags: (res) =>
        res ? [{ type: 'Tenant', id: 'intake-channels-logo' }] : [],
    }),
    //UPLOAD FILE FOR REPORT
    uploadReportFileProcess: builder.mutation<
      { id: number | undefined },
      FileUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/${arg.slug}/report-file/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startReportFileResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startReportFileResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startReportFileResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            baseURLOverride: startReportFileResObject.url,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            excludeAPIHeaders: true,
            withCredentials: false,
          });
          const finish = await baseQuery({
            url: `/${arg.slug}/report-file/upload/finish`,
            method: 'POST',
            data: {
              file_id: startReportFileResObject?.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startReportFileResObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: false },
      invalidatesTags: (res) =>
        res ? [{ type: 'Reports', id: 'casemanager-attachment' }] : [],
    },
    ),
    // UPLOAD VOICE REPORT
    uploadVoiceReportProcess: builder.mutation<
      { id: number },
      FileUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/${arg.slug}/voice-report-file/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startVoiceReportResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startVoiceReportResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startVoiceReportResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            baseURLOverride: startVoiceReportResObject.url,
            excludeAPIHeaders: true,
            withCredentials: false,
          });
          const finish = await baseQuery({
            url: `/${arg.slug}/voice-report-file/upload/finish`,
            method: 'POST',
            data: {
              file_id: startVoiceReportResObject?.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startRes.data.id ?? 0 } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: false },
    }),
    // UPDATE LOGO FOR TENANT
    uploadTenantLogoProcess: builder.mutation<
      { id: number | undefined },
      TenantLogoUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/tenant/${arg.slug}/tenant-logo/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startTenantLogoResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startTenantLogoResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startTenantLogoResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            baseURLOverride: startTenantLogoResObject.url,
            excludeAPIHeaders: true,
            withCredentials: false,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          const finish = await baseQuery({
            url: `/tenant/${arg.slug}/tenant-logo/upload/finish`,
            method: 'POST',
            data: {
              logo_id: startTenantLogoResObject?.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startTenantLogoResObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: true },
    }),
    // UPDATE BANNER FOR TENANT
    uploadTenantBannerProcess: builder.mutation<
      { id: number | undefined },
      TenantLogoUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/tenant/${arg.slug}/tenant-banner/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startTenantBannerResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startTenantBannerResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startTenantBannerResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            withCredentials: false,
            excludeAPIHeaders: true,
            baseURLOverride: startTenantBannerResObject.url,
          });
          const finish = await baseQuery({
            url: `/tenant/${arg.slug}/tenant-banner/upload/finish`,
            method: 'POST',
            data: {
              banner_id: startTenantBannerResObject?.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startTenantBannerResObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: true },
    }),
    // UPDATE TENANT INFOGRAPHIC
    uploadTenantInfographicProcess: builder.mutation<
      { url: string | undefined },
      TenantInfographicUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/intake-channel/tenant/${arg.slug}/${arg.selectedChannelId}/upload-start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startInfographicResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startInfographicResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startInfographicResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            excludeAPIHeaders: true,
            withCredentials: false,
            baseURLOverride: startInfographicResObject.url,
          });
          return { data: { url: startInfographicResObject?.fields?.key } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: true },
    }),
    // UPLOAD FILE IN REPORTING
    uploadReportingFile: builder.mutation<
      { id: number | undefined },
      ReportingUploadProcess
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/tenant/${arg.slug}/import-report-file/upload/start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startReportingFileResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startReportingFileResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startReportingFileResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          const upload = await baseQuery({
            url: '',
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            excludeAPIHeaders: true,
            baseURLOverride: startReportingFileResObject.url,
            withCredentials: false,
          });
          if (upload.error) {
            return { error: upload.error };
          }
          return { data: { id: startReportingFileResObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
    }),
    finishUploadReportingFile: builder.mutation<void, { slug: string, payload: { import_report_file_id: number } }>({
      query: (args) => {
        return {
          url: `/tenant/${args.slug}/import-report-file/upload/finish`,
          method: 'POST',
          data: args.payload,
        };
      },
    }),
    // UPLOAD FILE FOR PROCESS MANAGEMENT
    uploadFileForProcessComment: builder.mutation<
      { id: number | undefined },
      {
        file: File,
        startUploadData: {
          file_type: string,
          file_name: string,
          file_size: number,
        }
      }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: '/comment/upload-file-start',
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startCommentResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startCommentResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startCommentResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            baseURLOverride: startCommentResObject.url,
            excludeAPIHeaders: true,
            withCredentials: false,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          // Step 3: finish 
          const finish = await baseQuery({
            url: '/comment/upload-file-finish',
            method: 'POST',
            data: {
              file_id: startCommentResObject?.id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
          return { data: { id: startCommentResObject?.id } };
        }
        const error = {
          status: 'CUSTOM_ERROR',
          error: 'Something went wrong',
        } as FetchBaseQueryError;
        return { error };
      },
      extraOptions: { requireAuth: true },
    }),
    // UPLOAD IMAGE FOR BANNER TRANSLATION 
    UploadLocalizedBanerProcess: builder.mutation<
      { message: string },
      Omit<LogoBannerUploadProcess, 'uploadType'> & { main_id: number, language_id: number, type: 'tenant' | 'channel' }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        // Step 1: Start upload
        const start = await baseQuery({
          url: `/intake-channel/tenant/${arg.slug}/${arg.selectedChannelId}/upload-start`,
          method: 'POST',
          data: arg.startUploadData,
        });
        if (start.error) {
          return { error: start.error };
        }
        let startLogoBannerResObject: StartResponse;
        const startRes = startResponseSchema.safeParse(start.data);
        if (startRes.success) {
          startLogoBannerResObject = startRes.data;
          // Step 2: Upload to S3
          const formData = new FormData();
          Object.entries(startLogoBannerResObject.fields).forEach(([key, value]) => {
            formData.append(key, value);
          });
          formData.append('file', arg.file);
          await baseQuery({
            url: '',
            excludeAPIHeaders: true,
            baseURLOverride: startLogoBannerResObject.url,
            method: 'POST',
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            withCredentials: false,
          });
          // uplaod the banner depending on whether it is a tenant wide or channel specific
          const channelUrl = `/intake-channel/tenant/${arg.slug}/${arg.selectedChannelId}/localization/form-type-tenant-management`;
          const tenantUrl = '/localization/tenant';

          const finish = await baseQuery({
            url: arg.type === 'channel' ? channelUrl : tenantUrl,
            method: 'POST',
            data: {
              banner: startLogoBannerResObject.fields?.key,
              main_id: arg.main_id,
              language_id: arg.language_id,
            },
          });
          if (finish.error) {
            return { error: finish.error };
          }
        }
        return { data: { message: 'success' } };
      },
      extraOptions: { requireAuth: true },
      invalidatesTags: (res, _err, arg) => res && arg.type === 'tenant' ? [{ type: 'Localization', id: 'localized-tenant' }] : [],
    }),
  }),
  overrideExisting: false,
});

export const FileUploadService = endpoints;
